/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { Text } from '../UNSAFE_Text';
import { CurrentRowVariant, TableRendererContext } from '../UNSAFE_TableView';
import { TabbableModeContext } from '../hooks/UNSAFE_useTabbableMode';
import { classNames } from '../utils/UNSAFE_classNames';
import { useId } from '../hooks/UNSAFE_useId';
import { TableCellVariantOptions } from './themes/TableCellStyles.css';
import { TableCellRedwoodTheme } from './themes/redwood/TableCellTheme';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';

/**
 * Props for the TableCell Component
 */
export type TableCellProps<K, D, C> = {
  tableId: string;
  rowData: D;
  columnKey: C;
  columnIndex: number;
  rowKey: K;
  rowIndex: number;
  isFinalRowIndex: boolean;
  isFinalColumnIndex: boolean;
  hasVerticalGridlines: boolean;
  hasHorizontalGridlines: boolean;
  isRowSelected: boolean;
  isPreviousRowSelected: boolean;
  isColumnSelected: boolean;
  isPreviousColumnSelected: boolean;
  isFocusRingVisible: boolean;
  isTabbable: boolean;
  isStickyStartEdge: boolean;
  isStickyEndEdge: boolean;
  isRtl: boolean;
  isRowHeader: boolean;
  isRowSelectionEnabled: boolean;
  isRowSingleSelection: boolean;
  applyActiveStyle: boolean;
  applyHoverStyle: boolean;
  applyPseudoHoverStyle: boolean;
  field?: keyof D;
  stickyColumn?: 'start' | 'end' | 'none';
  renderer?: (context: TableRendererContext<K, D, C>) => ComponentChildren;
  selectorRenderer?: () => ComponentChildren;
  variant?: CurrentRowVariant;
};

function _defaultCellRenderer<K, D, C>({ rowData, field }: TableRendererContext<K, D, C>) {
  return field != null ? (
    <Text overflowWrap="breakWord" lineClamp={1} variant="primary" size="md">
      {String(rowData[field])}
    </Text>
  ) : null;
}

/**
 * The internal component used to render a single cell in a TableRow.
 */
export function TableCell<K, D, C>({
  tableId,
  rowData,
  field,
  columnKey,
  columnIndex,
  rowKey,
  rowIndex,
  isFinalRowIndex,
  isFinalColumnIndex,
  hasVerticalGridlines,
  hasHorizontalGridlines,
  isRowSelected,
  isPreviousRowSelected,
  isColumnSelected,
  isPreviousColumnSelected,
  isFocusRingVisible,
  isTabbable,
  isRowHeader,
  stickyColumn = 'none',
  renderer = _defaultCellRenderer,
  selectorRenderer,
  isRowSelectionEnabled,
  isRowSingleSelection,
  variant,
  applyActiveStyle,
  applyHoverStyle,
  applyPseudoHoverStyle,
  isStickyStartEdge,
  isStickyEndEdge,
  isRtl
}: TableCellProps<K, D, C>) {
  const hasRenderer = renderer !== _defaultCellRenderer;
  const { classes: themeClasses } = useComponentTheme<TableCellVariantOptions>(
    TableCellRedwoodTheme,
    {
      defaultRenderer: !hasRenderer ? 'isDefaultRenderer' : 'notDefaultRenderer',
      firstRow: rowIndex === 0 ? 'isFirstRow' : 'notFirstRow',
      lastRow: isFinalRowIndex ? 'isLastRow' : 'notLastRow',
      firstColumn: columnIndex === 0 ? 'isFirstColumn' : 'notFirstColumn',
      lastColumn: isFinalColumnIndex ? 'isLastColumn' : 'notLastColumn',
      borderTopSpacer:
        !(isRowSingleSelection && (isRowSelected || isPreviousRowSelected)) &&
        (!hasHorizontalGridlines || rowIndex === 0)
          ? 'isBorderTopSpacer'
          : 'notBorderTopSpacer',
      borderBottomSpacer:
        isFinalRowIndex && !((isRowSingleSelection && isRowSelected) || hasHorizontalGridlines)
          ? 'isBorderBottomSpacer'
          : 'notBorderBottomSpacer',
      borderStartSpacer:
        !(isPreviousColumnSelected || isColumnSelected) &&
        (!hasVerticalGridlines || columnIndex === 0)
          ? 'isBorderStartSpacer'
          : 'notBorderStartSpacer',
      borderEndSpacer:
        isFinalColumnIndex && !isColumnSelected ? 'isBorderEndSpacer' : 'notBorderEndSpacer',
      verticalGrid: hasVerticalGridlines ? 'isVerticalGrid' : 'notVerticalGrid',
      horizontalGrid: hasHorizontalGridlines ? 'isHorizontalGrid' : 'notHorizontalGrid',
      rowSingleSelection: isRowSingleSelection ? 'isRowSingleSelection' : 'notRowSingleSelection',
      rowSelectable: isRowSelectionEnabled ? 'isRowSelectable' : 'notRowSelectable',
      rowSelected: isRowSelected ? 'isRowSelected' : 'notRowSelected',
      previousRowSelected: isPreviousRowSelected
        ? 'isPreviousRowSelected'
        : 'notPreviousRowSelected',
      columnSelected: isColumnSelected ? 'isColumnSelected' : 'notColumnSelected',
      previousColumnSelected: isPreviousColumnSelected
        ? 'isPreviousColumnSelected'
        : 'notPreviousColumnSelected',
      stickyStartColumn: stickyColumn === 'start' ? 'isStickyStartColumn' : 'notStickyStartColumn',
      stickyEndColumn: stickyColumn === 'end' ? 'isStickyEndColumn' : 'notStickyEndColumn',
      stickyStartEdge: isStickyStartEdge ? 'isStickyStartEdge' : 'notStickyStartEdge',
      stickyEndEdge: isStickyEndEdge ? 'isStickyEndEdge' : 'notStickyEndEdge',
      rowHighlight: variant === 'highlight' ? 'isRowHighlight' : 'notRowHighlight',
      focusRingVisible: isFocusRingVisible ? 'isFocusRingVisible' : 'notFocusRingVisible',
      active: applyActiveStyle ? 'isActive' : 'notActive',
      hover: applyHoverStyle ? 'isHover' : 'notHover',
      pseudoHover: applyPseudoHoverStyle ? 'isPseudoHover' : 'notPseudoHover',
      rtl: isRtl ? 'isRtl' : 'notRtl'
    }
  );

  const cellClasses = classNames([themeClasses]);

  const cellRendererProps = {
    rowData: rowData,
    rowKey: rowKey,
    field: field,
    columnKey: columnKey,
    selector: selectorRenderer
  };

  return (
    <td
      aria-colindex={columnIndex + 1}
      class={cellClasses}
      id={useId()}
      tabIndex={-1}
      role={isRowHeader ? 'rowheader' : 'gridcell'}
      data-oj-cell-type={'data'}
      data-oj-column-key={columnKey}
      data-oj-table-focusable={tableId}
      data-oj-table-data-cell={tableId}
      {...(typeof columnKey === 'number' && { 'data-oj-column-key-type': 'number' })}>
      <TabbableModeContext.Provider value={{ isTabbable }}>
        {renderer(cellRendererProps)}
      </TabbableModeContext.Provider>
    </td>
  );
}
